import { useState, useEffect, useRef } from "react";
import { Sparkles } from "lucide-react";
import { useCountUp } from "@/lib/useCountUp";
import { useCardTilt } from "@/lib/useCardTilt";

// ---------- Tax-Bracket Escape Ring — sapphire→emerald gradient stroke meter ----------
// Milestone chime — synthesized on-the-fly via WebAudio so we ship zero audio assets.
// 90% plays a 2-note major-third arpeggio; 100% plays a 3-note triumphant triad.
function playMilestoneChime(step) {
  try {
    if (typeof window === "undefined") return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    // Respect the user having tabbed away or muted the browser (autoplay policy)
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const notes = step >= 100 ? [523.25, 659.25, 783.99] : [523.25, 659.25];
    const gapMs = 130;
    notes.forEach((freq, i) => {
      const t0 = ctx.currentTime + (i * gapMs) / 1000;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, t0);
      // Soft attack + slow decay so the chime sits under the confetti visually
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.16, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.55);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.6);
    });
    // Best-effort context teardown
    setTimeout(() => { try { ctx.close(); } catch (_) { /* noop */ } }, 1200);
  } catch (_) { /* silent — chime is a nice-to-have */ }
}

export function TaxBracketEscapeRing({ stats }) {
  const gross = Number(stats?.total_spent || 0);
  const deductible = Number(stats?.deductible || 0);
  const escapePct = gross > 0 ? Math.min(1, deductible / gross) : 0;
  const animated = useCountUp(Math.round(escapePct * 100), 1200);
  const [pulseKey, setPulseKey] = useState(0);
  const [confettiKey, setConfettiKey] = useState(0);
  const [milestone, setMilestone] = useState(0); // last milestone fired (0/50/75/90/100)
  const firedRef = useRef(new Set());
  // Every time deductible changes, kick a fresh emerald-glow pulse
  useEffect(() => {
    if (stats) setPulseKey((k) => k + 1);
  }, [deductible, stats]);
  // Progressive milestone ladder — richer bursts as the ring climbs
  useEffect(() => {
    const LADDER = [50, 75, 90, 100];
    for (const step of LADDER) {
      if (animated >= step && !firedRef.current.has(step)) {
        firedRef.current.add(step);
        setMilestone(step);
        setConfettiKey((k) => k + 1);
        // Milestone sound cue — chime layer at 90 and 100 only (subtle, non-intrusive)
        if (step >= 90) playMilestoneChime(step);
      }
    }
  }, [animated]);
  // Piece count scales with the milestone
  const MILESTONE_PIECES = { 50: 10, 75: 16, 90: 22, 100: 30 };
  const pieces = MILESTONE_PIECES[milestone] || 14;

  const size = 160;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = circ * (animated / 100);

  // 3D parallax tilt + numeric-change ripple (fires on escape % change)
  const { bind, Ripples } = useCardTilt({ pulseKey: animated });

  return (
    <div
      {...bind}
      data-testid="tax-escape-ring"
      className="glass cosmic-aura cosmic-aura-emerald cosmic-sheen rounded-2xl p-6 relative overflow-hidden flex flex-col items-center justify-center"
      style={{ ...bind.style, borderColor: "rgba(16,185,129,0.28)" }}
    >
      <Ripples />
      <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-emerald-400 mb-2 flex items-center gap-1.5">
        <Sparkles size={11} /> Tax-Bracket Escape
      </div>
      <div
        key={pulseKey}
        className="tax-escape-ring-pulse relative"
        style={{ width: size, height: size }}
        data-testid="tax-escape-ring-svg-wrap"
      >
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="rotate-[-90deg]">
          <defs>
            <linearGradient id="tax-escape-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#1D4ED8" />
              <stop offset="100%" stopColor="#10B981" />
            </linearGradient>
          </defs>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none"
                  stroke="rgba(148,163,184,0.15)" strokeWidth={stroke} />
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none"
            stroke="url(#tax-escape-grad)"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circ - dash}`}
            style={{ transition: "stroke-dasharray 900ms cubic-bezier(0.22,1,0.36,1)",
                     filter: "drop-shadow(0 0 8px rgba(16,185,129,0.55))" }}
          />
        </svg>
        {/* Neon-cyan comet spark that continuously sweeps the outer circumference */}
        <svg
          aria-hidden="true"
          data-testid="tax-escape-comet"
          width={size} height={size} viewBox={`0 0 ${size} ${size}`}
          className="cosmic-comet-orbit"
        >
          <defs>
            <radialGradient id="tax-escape-comet-grad" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor="#22d3ee" stopOpacity="1" />
              <stop offset="55%" stopColor="#34d399" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="tax-escape-comet-tail" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"  stopColor="#10b981" stopOpacity="0" />
              <stop offset="60%" stopColor="#34d399" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.85" />
            </linearGradient>
          </defs>
          {/* Trailing tail arc — faint neon-cyan stroke that fades in ~25° behind the head */}
          <path
            d={`M ${size / 2} ${stroke / 2 + 2}
                A ${r} ${r} 0 0 1 ${size / 2 + Math.sin((25 * Math.PI) / 180) * r} ${size / 2 - Math.cos((25 * Math.PI) / 180) * r}`}
            fill="none"
            stroke="url(#tax-escape-comet-tail)"
            strokeWidth="2.5"
            strokeLinecap="round"
            style={{ filter: "drop-shadow(0 0 6px rgba(34,211,238,0.8))" }}
            transform={`rotate(-25 ${size / 2} ${size / 2})`}
          />
          {/* Comet head */}
          <circle
            cx={size / 2} cy={stroke / 2 + 2} r="5"
            fill="url(#tax-escape-comet-grad)"
            style={{ filter: "drop-shadow(0 0 10px rgba(34,211,238,0.95))" }}
          />
          <circle cx={size / 2} cy={stroke / 2 + 2} r="1.6" fill="#ecfeff" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-mono font-bold text-white cosmic-digit-glow" style={{ fontSize: 38, lineHeight: 1 }}>
            {Math.round(animated)}<span className="text-slate-500 text-xl">%</span>
          </div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-1">
            of spend written off
          </div>
        </div>
        {/* Milestone confetti ladder — richer burst per threshold (50 / 75 / 90 / 100) */}
        {confettiKey > 0 && (
          <div
            key={confettiKey}
            data-testid="tax-escape-confetti"
            data-milestone={milestone}
            className="pointer-events-none absolute inset-0 tax-escape-confetti-burst"
            aria-hidden="true"
          >
            {Array.from({ length: pieces }).map((_, i) => {
              const angle = (i / pieces) * Math.PI * 2;
              const spread = 70 + milestone * 0.6; // fly farther on higher milestones
              const dx = Math.cos(angle) * spread;
              const dy = Math.sin(angle) * spread;
              const rot = (i * 47) % 360;
              const palette =
                milestone >= 100
                  ? ["#FFD166", "#F59E0B", "#10B981", "#34D399", "#22d3ee", "#a78bfa"]
                  : milestone >= 90
                  ? ["#10B981", "#34D399", "#F59E0B", "#22d3ee", "#a78bfa"]
                  : milestone >= 75
                  ? ["#10B981", "#34D399", "#1D4ED8", "#F59E0B", "#22d3ee"]
                  : ["#10B981", "#34D399", "#1D4ED8"];
              const color = palette[i % palette.length];
              return (
                <span
                  key={i}
                  className="tax-escape-confetti-piece absolute left-1/2 top-1/2"
                  style={{
                    "--dx": `${dx}px`,
                    "--dy": `${dy}px`,
                    "--rot": `${rot}deg`,
                    background: color,
                    animationDelay: `${(i % 5) * 40}ms`,
                  }}
                />
              );
            })}
            {/* Milestone label — bright emerald tag that fades with the burst */}
            <span
              className="tax-escape-milestone-tag absolute left-1/2 top-full -translate-x-1/2 mt-2 font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full"
              style={{
                background: "rgba(16,185,129,0.15)",
                border: "1px solid rgba(16,185,129,0.55)",
                color: "#a7f3d0",
                whiteSpace: "nowrap",
              }}
              data-testid={`tax-escape-milestone-${milestone}`}
            >
              {milestone === 100 ? "Bracket Escaped!" : `${milestone}% Milestone`}
            </span>
          </div>
        )}
      </div>
      <div className="text-[10px] font-mono text-slate-400 mt-3 text-center max-w-[220px] leading-relaxed">
        Higher ring = more of every dollar working as a deduction. Escape the bracket.
      </div>
      <style>{`
        @keyframes tax-escape-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(16,185,129,0.7), inset 0 0 0 rgba(16,185,129,0); }
          70%  { box-shadow: 0 0 0 22px rgba(16,185,129,0), inset 0 0 24px rgba(16,185,129,0.35); }
          100% { box-shadow: 0 0 0 0 rgba(16,185,129,0), inset 0 0 0 rgba(16,185,129,0); }
        }
        .tax-escape-ring-pulse { border-radius: 9999px; animation: tax-escape-pulse 1.8s ease-out; }
        /* 75% milestone confetti — 14 pieces radiating out and fading */
        .tax-escape-confetti-piece {
          width: 8px;
          height: 3px;
          border-radius: 2px;
          transform: translate(-50%, -50%) rotate(0deg);
          opacity: 0;
          animation: tax-escape-confetti-fly 1400ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
          box-shadow: 0 0 6px currentColor;
        }
        @keyframes tax-escape-confetti-fly {
          0%   { transform: translate(-50%, -50%) rotate(0deg) scale(0.6); opacity: 0; }
          10%  { opacity: 1; }
          60%  { opacity: 1; }
          100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) rotate(var(--rot)) scale(1); opacity: 0; }
        }
        .tax-escape-milestone-tag {
          opacity: 0;
          animation: tax-escape-tag 1600ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes tax-escape-tag {
          0%   { opacity: 0; transform: translate(-50%, -6px); }
          15%  { opacity: 1; transform: translate(-50%, 0); }
          75%  { opacity: 1; transform: translate(-50%, 0); }
          100% { opacity: 0; transform: translate(-50%, -4px); }
        }
      `}</style>
    </div>
  );
}


